import { Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';

interface RelatedBlogPostsProps {
  city: string;
}

// City-to-blog-posts mapping
const cityBlogMapping: Record<string, string[]> = {
  toronto: [
    'best-alarm-system-company-toronto',
    'best-home-security-system-toronto',
    'commercial-security-camera-system-toronto',
  ],
  mississauga: [
    'best-alarm-system-company-mississauga',
    'how-to-choose-alarm-system-mississauga',
    'commercial-security-company-mississauga',
  ],
  brampton: [
    'best-alarm-system-company-brampton',
    'brampton-retail-store-security-case-study',
    'commercial-security-company-brampton',
  ],
  oakville: ['best-alarm-system-company-oakville', 'best-home-security-company-oakville', 'commercial-security-company-oakville'],
  hamilton: ['best-alarm-system-company-hamilton', 'best-home-security-company-hamilton', 'commercial-security-company-hamilton'],
  'richmond-hill': ['best-alarm-system-company-richmond-hill'],
  vaughan: ['best-alarm-system-company-vaughan'],
  whitby: [
    'top-10-home-security-tips-gta-2024',
    'wired-vs-wireless-security-systems-canada',
    'home-security-insurance-discounts-ontario',
  ],
  ajax: [
    'top-10-home-security-tips-gta-2024',
    'wired-vs-wireless-security-systems-canada',
    'home-security-insurance-discounts-ontario',
  ],
  oshawa: [
    'top-10-home-security-tips-gta-2024',
    'wired-vs-wireless-security-systems-canada',
    'home-security-insurance-discounts-ontario',
  ],
  'service-areas': [
    'top-10-home-security-tips-gta-2024',
    'best-alarm-system-company-toronto',
    'wired-vs-wireless-security-systems-canada',
  ],
};

const RelatedBlogPosts: React.FC<RelatedBlogPostsProps> = ({ city }) => {
  const cityKey = city.toLowerCase().replace(/\s+/g, '-');
  const relatedSlugs = cityBlogMapping[cityKey] || [];

  // Filter blog posts based on the mapping
  const relatedPosts = blogPosts.filter((post) =>
    relatedSlugs.includes(post.slug)
  );

  if (relatedPosts.length === 0) {
    return null;
  }

  const displayCity = city
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-full mb-4">
            <span className="text-sm font-semibold text-amber-600 uppercase tracking-wide">
              Related Guides for {displayCity}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Expert Security Insights
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover helpful guides and tips tailored for {displayCity} residents
            and businesses
          </p>
        </div>

        {/* Blog Post Cards Grid */}
        <div
          className={`grid gap-8 ${
            relatedPosts.length === 1
              ? 'md:grid-cols-1 max-w-2xl mx-auto'
              : relatedPosts.length === 2
              ? 'md:grid-cols-2'
              : 'md:grid-cols-3'
          }`}
        >
          {relatedPosts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              {/* Post Image */}
              <div className="relative h-56 overflow-hidden bg-gray-100">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full uppercase tracking-wide">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Post Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Meta Info */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </div>

                {/* Read More Link */}
                <div className="flex items-center gap-2 text-amber-600 font-semibold group-hover:gap-3 transition-all">
                  <span>Read Article</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedBlogPosts;